import { useState, useEffect, useCallback } from 'react';
import {
  Calculator,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Info,
  Play,
} from 'lucide-react';
import clsx from 'clsx';
import { periodDisplayName, type Period, type TaxComputeResponse, type EntityTaxDetail, type QfzpEntityStatus } from '@/types';
import { taxApi, periodsApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Format helpers ----------------------------------------------------------

function formatAmount(amount: number): string {
  if (amount === 0) return '\u2014';
  const abs = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(abs);
  return amount < 0 ? `(${formatted})` : formatted;
}

function formatPct(pct: number): string {
  return `${(pct * 100).toFixed(2)}%`;
}

function formatPctDirect(pct: number): string {
  return `${pct.toFixed(2)}%`;
}

// --- Tab types ---------------------------------------------------------------

type TaxTab = 'summary' | 'qfzp' | 'sbr';

// --- Main Page ---------------------------------------------------------------

export default function TaxComputation() {
  const { addNotification } = useAppStore();

  const [activeTab, setActiveTab] = useState<TaxTab>('summary');
  const [isLoading, setIsLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Period selector
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');

  // Data
  const [taxResults, setTaxResults] = useState<TaxComputeResponse | null>(null);
  const [qfzpStatuses, setQfzpStatuses] = useState<QfzpEntityStatus[]>([]);
  const [sbrEntities, setSbrEntities] = useState<EntityTaxDetail[]>([]);

  // Expanded rows
  const [expandedEntity, setExpandedEntity] = useState<number | null>(null);

  // Load periods
  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const res = await periodsApi.list();
        setPeriods(res);
        const openPeriod = res.find((p) => p.status === 'open') || res[0];
        if (openPeriod) setSelectedPeriod(openPeriod.id);
      } catch {
        // silently fail
      }
    };
    loadPeriods();
  }, []);

  // Load tax results for selected period
  const loadResults = useCallback(async () => {
    if (!selectedPeriod) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const [taxRes, qfzpRes, sbrRes] = await Promise.all([
        taxApi.results(Number(selectedPeriod)).catch(() => null),
        taxApi.qfzpStatus().catch(() => []),
        taxApi.sbrEligible().catch(() => []),
      ]);
      setTaxResults(taxRes);
      setQfzpStatuses(qfzpRes);
      setSbrEntities(sbrRes);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load tax data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    if (selectedPeriod) loadResults();
  }, [loadResults, selectedPeriod]);

  // Compute tax
  const handleCompute = async () => {
    if (!selectedPeriod) return;
    setComputing(true);
    try {
      const result = await taxApi.compute({ period_id: Number(selectedPeriod) });
      setTaxResults(result);
      addNotification({ type: 'success', title: 'Tax Computed', message: 'UAE Corporate Tax computation completed successfully.' });
      // Reload subsidiary data
      const [qfzpRes, sbrRes] = await Promise.all([
        taxApi.qfzpStatus().catch(() => []),
        taxApi.sbrEligible().catch(() => []),
      ]);
      setQfzpStatuses(qfzpRes);
      setSbrEntities(sbrRes);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Computation Failed', message: apiErr.detail || 'Tax computation failed.' });
    } finally {
      setComputing(false);
    }
  };

  const tabs: { id: TaxTab; label: string }[] = [
    { id: 'summary', label: 'Tax Summary' },
    { id: 'qfzp', label: 'QFZP Status' },
    { id: 'sbr', label: 'SBR Eligibility' },
  ];

  // Loading skeleton
  if (isLoading && !taxResults) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <Calculator className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <div className="skeleton w-48 h-7 mb-1" />
            <div className="skeleton w-64 h-4" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card p-5 animate-pulse">
              <div className="skeleton w-28 h-3 mb-3" />
              <div className="skeleton w-20 h-8" />
            </div>
          ))}
        </div>
        <div className="card p-6 animate-pulse">
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton w-full h-10" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <Calculator className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">UAE Corporate Tax</h1>
            <p className="text-sm text-gray-500 mt-0.5">Tax computation, QFZP status, and SBR eligibility</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="select-field text-sm w-40"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            {periods.length === 0 && <option value="">No periods</option>}
            {periods.map((p) => (
              <option key={p.id} value={p.id}>{periodDisplayName(p)}</option>
            ))}
          </select>
          <button
            onClick={handleCompute}
            disabled={computing || !selectedPeriod}
            className="btn-primary"
          >
            {computing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Compute Tax
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadResults} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-6 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'pb-3 text-sm font-medium border-b-2 transition-colors -mb-px',
              activeTab === tab.id
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ═══════════════════════ TAX SUMMARY TAB ═══════════════════════ */}
      {activeTab === 'summary' && (
        <>
          {!taxResults ? (
            <div className="card py-16 text-center">
              <Calculator className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <h3 className="text-base font-medium text-gray-900">No tax computation available</h3>
              <p className="text-sm text-gray-500 mt-1">
                Select a period and click "Compute Tax" to generate results.
              </p>
            </div>
          ) : (
            <>
              {/* Group KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card p-5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Taxable Income</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1 font-mono">
                    {formatAmount(taxResults.group_total?.total_taxable_income ?? 0)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Group consolidated</p>
                </div>
                <div className="card p-5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Tax Liability</p>
                  <p className={clsx(
                    'text-2xl font-bold mt-1 font-mono',
                    Number(taxResults.group_total?.total_tax_liability ?? 0) > 0 ? 'text-red-700' : 'text-gray-900',
                  )}>
                    {formatAmount(Number(taxResults.group_total?.total_tax_liability ?? 0))}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">AED</p>
                </div>
                <div className="card p-5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Group Effective Rate</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1 font-mono">
                    {formatPct(taxResults.group_total?.group_effective_rate ?? 0)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Weighted average</p>
                </div>
              </div>

              {/* Entity Tax Table */}
              <div className="card overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
                  <h2 className="text-base font-semibold text-gray-900">Entity Tax Breakdown</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="table-header w-8"></th>
                        <th className="table-header">Entity</th>
                        <th className="table-header text-right w-28">Acctg Profit</th>
                        <th className="table-header text-right w-24">Add-backs</th>
                        <th className="table-header text-right w-24">Exemptions</th>
                        <th className="table-header text-right w-28">Taxable Income</th>
                        <th className="table-header text-right w-16">Rate</th>
                        <th className="table-header text-right w-24">Tax Liability</th>
                        <th className="table-header text-right w-20">Eff. Rate</th>
                        <th className="table-header w-20">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(taxResults.entity_results ?? []).map((entity) => {
                        const isExpanded = expandedEntity === entity.entity_id;
                        const addBackTotal = entity.add_backs.reduce((s, a) => s + a.amount, 0);
                        const exemptTotal = entity.exempt_income.reduce((s, e) => s + e.amount, 0);

                        return (
                          <>
                            <tr
                              key={entity.entity_id}
                              className={clsx(
                                'border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer transition-colors',
                                entity.is_qfzp && 'bg-green-50/30',
                                entity.sbr_eligible && !entity.is_qfzp && 'bg-blue-50/30',
                              )}
                              onClick={() => setExpandedEntity(isExpanded ? null : entity.entity_id)}
                            >
                              <td className="table-cell">
                                {isExpanded ? (
                                  <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                                ) : (
                                  <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                                )}
                              </td>
                              <td className="table-cell text-sm font-medium text-gray-800">
                                {entity.entity_name}
                              </td>
                              <td className="table-cell text-right font-mono text-sm text-gray-700">
                                {formatAmount(entity.accounting_profit)}
                              </td>
                              <td className="table-cell text-right font-mono text-sm text-gray-600">
                                {formatAmount(addBackTotal)}
                              </td>
                              <td className={clsx(
                                'table-cell text-right font-mono text-sm',
                                exemptTotal < 0 ? 'text-financial-negative' : 'text-gray-600',
                              )}>
                                {formatAmount(exemptTotal)}
                              </td>
                              <td className="table-cell text-right font-mono text-sm font-semibold text-gray-900">
                                {formatAmount(entity.taxable_income)}
                              </td>
                              <td className="table-cell text-right font-mono text-sm text-gray-600">
                                {formatPct(entity.tax_rate)}
                              </td>
                              <td className={clsx(
                                'table-cell text-right font-mono text-sm font-semibold',
                                entity.tax_liability > 0 ? 'text-financial-negative' : 'text-gray-900',
                              )}>
                                {formatAmount(entity.tax_liability)}
                              </td>
                              <td className="table-cell text-right font-mono text-xs text-gray-500">
                                {formatPct(entity.effective_tax_rate)}
                              </td>
                              <td className="table-cell">
                                {entity.is_qfzp && (
                                  <span className="badge bg-green-50 text-green-700">QFZP</span>
                                )}
                                {entity.sbr_eligible && !entity.is_qfzp && (
                                  <span className="badge bg-blue-50 text-blue-700">SBR</span>
                                )}
                                {!entity.is_qfzp && !entity.sbr_eligible && (
                                  <span className="badge bg-gray-50 text-gray-600">Standard</span>
                                )}
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr key={`${entity.entity_id}-detail`} className="bg-gray-50/80">
                                <td colSpan={10} className="px-8 py-4">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Add-backs */}
                                    <div>
                                      <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Add-backs</h4>
                                      {entity.add_backs.length === 0 ? (
                                        <p className="text-xs text-gray-400 italic">No add-backs</p>
                                      ) : (
                                        <div className="space-y-1">
                                          {entity.add_backs.map((ab, idx) => (
                                            <div key={idx} className="flex items-center justify-between text-sm">
                                              <span className="text-gray-600">{ab.description}</span>
                                              <span className="font-mono text-gray-800">{formatAmount(ab.amount)}</span>
                                            </div>
                                          ))}
                                          <div className="flex items-center justify-between text-sm font-semibold border-t border-gray-200 pt-1">
                                            <span className="text-gray-700">Total Add-backs</span>
                                            <span className="font-mono text-gray-900">{formatAmount(addBackTotal)}</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    {/* Exempt Income */}
                                    <div>
                                      <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Exempt Income</h4>
                                      {entity.exempt_income.length === 0 ? (
                                        <p className="text-xs text-gray-400 italic">No exemptions</p>
                                      ) : (
                                        <div className="space-y-1">
                                          {entity.exempt_income.map((ex, idx) => (
                                            <div key={idx} className="flex items-center justify-between text-sm">
                                              <span className="text-gray-600">{ex.description}</span>
                                              <span className="font-mono text-gray-800">{formatAmount(ex.amount)}</span>
                                            </div>
                                          ))}
                                          <div className="flex items-center justify-between text-sm font-semibold border-t border-gray-200 pt-1">
                                            <span className="text-gray-700">Total Exemptions</span>
                                            <span className="font-mono text-gray-900">{formatAmount(exemptTotal)}</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })}
                    </tbody>
                    {/* Group totals */}
                    <tfoot>
                      <tr className="border-t-2 border-gray-300 bg-gray-50">
                        <td className="table-cell"></td>
                        <td className="table-cell text-sm font-bold text-gray-900">Group Total</td>
                        <td className="table-cell"></td>
                        <td className="table-cell"></td>
                        <td className="table-cell"></td>
                        <td className="table-cell text-right font-mono text-sm font-bold text-gray-900">
                          {formatAmount(taxResults.group_total?.total_taxable_income ?? 0)}
                        </td>
                        <td className="table-cell"></td>
                        <td className="table-cell text-right font-mono text-sm font-bold text-red-700">
                          {formatAmount(Number(taxResults.group_total?.total_tax_liability ?? 0))}
                        </td>
                        <td className="table-cell text-right font-mono text-xs font-bold text-gray-700">
                          {formatPct(taxResults.group_total?.group_effective_rate ?? 0)}
                        </td>
                        <td className="table-cell"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ═══════════════════════ QFZP STATUS TAB ═══════════════════════ */}
      {activeTab === 'qfzp' && (
        <>
          {/* Info box */}
          <div className="card p-4 border-blue-200 bg-blue-50/50">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Qualifying Free Zone Person (QFZP) Rules</p>
                <ul className="list-disc pl-4 space-y-0.5 text-xs text-blue-700">
                  <li>Free zone entities can benefit from 0% corporate tax on qualifying income.</li>
                  <li>Non-qualifying income must be below 5% of total income OR less than AED 5,000,000 (de minimis test).</li>
                  <li>Entities must maintain adequate substance and comply with transfer pricing documentation.</li>
                  <li>Non-qualifying income is taxed at the standard 9% rate.</li>
                </ul>
              </div>
            </div>
          </div>

          {qfzpStatuses.length === 0 ? (
            <div className="card py-16 text-center">
              <Calculator className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <h3 className="text-base font-medium text-gray-900">No QFZP data available</h3>
              <p className="text-sm text-gray-500 mt-1">
                Run a tax computation first to see QFZP status for free zone entities.
              </p>
            </div>
          ) : (
            <div className="card overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
                <h2 className="text-base font-semibold text-gray-900">Free Zone Entity Status</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="table-header">Entity</th>
                      <th className="table-header text-right w-32">Qualifying Income</th>
                      <th className="table-header text-right w-36">Non-Qualifying Income</th>
                      <th className="table-header text-right w-28">Total</th>
                      <th className="table-header text-right w-28">Non-Qualifying %</th>
                      <th className="table-header w-28">De Minimis Test</th>
                      <th className="table-header w-24">QFZP Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {qfzpStatuses.map((entity) => {
                      const total = entity.qualifying_income + entity.non_qualifying_income;
                      const nonQualPct = total > 0 ? (entity.non_qualifying_income / total) * 100 : 0;

                      return (
                        <tr
                          key={entity.entity_id}
                          className={clsx(
                            'border-b border-gray-100 hover:bg-gray-50/50 transition-colors',
                            entity.is_qfzp && entity.de_minimis_pass && 'bg-green-50/20',
                          )}
                        >
                          <td className="table-cell text-sm font-medium text-gray-800">{entity.entity_name}</td>
                          <td className="table-cell text-right font-mono text-sm text-gray-700">
                            {formatAmount(entity.qualifying_income)}
                          </td>
                          <td className="table-cell text-right font-mono text-sm text-gray-700">
                            {formatAmount(entity.non_qualifying_income)}
                          </td>
                          <td className="table-cell text-right font-mono text-sm font-semibold text-gray-900">
                            {formatAmount(total)}
                          </td>
                          <td className={clsx(
                            'table-cell text-right font-mono text-sm',
                            nonQualPct > 5 ? 'text-red-600 font-semibold' : 'text-green-600',
                          )}>
                            {formatPctDirect(nonQualPct)}
                          </td>
                          <td className="table-cell">
                            {entity.de_minimis_pass ? (
                              <span className="badge bg-green-50 text-green-700 gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Pass
                              </span>
                            ) : (
                              <span className="badge bg-red-50 text-red-700 gap-1">
                                <XCircle className="w-3 h-3" />
                                Fail
                              </span>
                            )}
                          </td>
                          <td className="table-cell">
                            {entity.is_qfzp && entity.de_minimis_pass ? (
                              <CheckCircle2 className="w-5 h-5 text-green-600" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-500" />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════════════════════ SBR ELIGIBILITY TAB ═══════════════════════ */}
      {activeTab === 'sbr' && (
        <>
          {/* Info box */}
          <div className="card p-4 border-blue-200 bg-blue-50/50">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Small Business Relief (SBR)</p>
                <p className="text-xs text-blue-700">
                  Entities with revenue not exceeding AED 3,000,000 in the relevant tax period may elect for Small Business Relief,
                  resulting in 0% taxable income. This election must be made per entity and is subject to conditions.
                </p>
              </div>
            </div>
          </div>

          {sbrEntities.length === 0 ? (
            <div className="card py-16 text-center">
              <Calculator className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <h3 className="text-base font-medium text-gray-900">No SBR eligible entities</h3>
              <p className="text-sm text-gray-500 mt-1">
                No entities with revenue at or below AED 3M were found, or tax computation has not been run.
              </p>
            </div>
          ) : (
            <>
              {/* Potential Savings Card */}
              <div className="card p-5 border-green-200 bg-green-50/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-green-700 uppercase tracking-wider">Total Potential Tax Savings</p>
                    <p className="text-3xl font-bold text-green-800 mt-1 font-mono">
                      {formatAmount(sbrEntities.reduce((s, e) => s + e.tax_liability, 0))}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      If all eligible entities elect SBR
                    </p>
                  </div>
                  <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center">
                    <Calculator className="w-7 h-7 text-green-700" />
                  </div>
                </div>
              </div>

              <div className="card overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
                  <h2 className="text-base font-semibold text-gray-900">SBR Eligible Entities</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="table-header">Entity</th>
                        <th className="table-header text-right w-28">Revenue</th>
                        <th className="table-header text-right w-28">Taxable Income</th>
                        <th className="table-header text-right w-28">Tax (No SBR)</th>
                        <th className="table-header text-right w-28">Tax (With SBR)</th>
                        <th className="table-header text-right w-28">Potential Saving</th>
                        <th className="table-header w-24">SBR Election</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sbrEntities.map((entity) => (
                        <tr
                          key={entity.entity_id}
                          className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors"
                        >
                          <td className="table-cell text-sm font-medium text-gray-800">{entity.entity_name}</td>
                          <td className="table-cell text-right font-mono text-sm text-gray-700">
                            {formatAmount(entity.accounting_profit)}
                          </td>
                          <td className="table-cell text-right font-mono text-sm text-gray-700">
                            {formatAmount(entity.taxable_income)}
                          </td>
                          <td className="table-cell text-right font-mono text-sm text-financial-negative">
                            {formatAmount(entity.tax_liability)}
                          </td>
                          <td className="table-cell text-right font-mono text-sm font-semibold text-green-700">
                            0
                          </td>
                          <td className="table-cell text-right font-mono text-sm font-semibold text-green-700">
                            {formatAmount(entity.tax_liability)}
                          </td>
                          <td className="table-cell text-center">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                defaultChecked={entity.sbr_eligible}
                                className="sr-only peer"
                                onChange={async (e) => {
                                  try {
                                    await taxApi.adjustments(entity.entity_id, {
                                      sbr_elected: e.target.checked,
                                    });
                                    addNotification({
                                      type: 'success',
                                      title: 'SBR Updated',
                                      message: `SBR ${e.target.checked ? 'elected' : 'un-elected'} for ${entity.entity_name}`,
                                    });
                                  } catch {
                                    addNotification({
                                      type: 'error',
                                      title: 'Update Failed',
                                      message: 'Failed to update SBR election.',
                                    });
                                  }
                                }}
                              />
                              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                            </label>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
